import { workspaceTextFor, type WorkspaceText } from './interface-copy';
import { useWorkspaceText } from './interface-copy';
import { FileReadability } from './file-readability';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeftIcon, Maximize2Icon, Minimize2Icon } from 'lucide-react';
import { CanvasSheetHandle, CanvasSheetScrim, useCanvasSheet } from '@/mobile/canvas-sheet';
import { useWorkspace } from './context';
import { useCompany } from '@/company/context';
import { downloadFile, workspaceApi } from './api';
import { WorkspaceIcon } from './icons';
import { dateLabel, ErrorState, LoadingState, sizeLabel } from './primitives';
import { safeSourceUrl } from './markdown';
import { SkillCanvas } from './skill-canvas';
import { ResultCanvas } from './result-canvas';
import { DeleteLibraryItem } from './item-archive/delete-item';
import { parseFileCsv } from './csv';
import { tableChart, WorkspaceChart, WorkspaceTable } from './table';
import type { CompanyFile, CompanyResult, CompanySkill, CompanySource } from './types';
import type { CompanyMeeting } from './types';
import { MeetingCanvas, meetingNeedsRefresh } from './meeting-canvas';
import { announceMeetingUpdated } from './meeting-events';
import { sourcePresentation } from './knowledge-sources';
import { emptyTurnContext } from './types';
import { SourceText } from './source-text';
import { ShareButton } from './sharing';
import { audienceLabel } from './sharing-api';
import { KnowledgePageCanvas, KnowledgeRootCanvas, PageProposalCanvas } from './knowledge-pages';
import { meetingShareItem } from './meeting-sharing';
import { VoicePreview } from './voice-preview';
import { CanvasLeaveDialog } from './canvas-leave-dialog';
import { CanvasFooterHost, CanvasHeaderHost } from './canvas-footer';
import { CanvasHistoryArrows, CanvasSwitcher } from './canvas-history-nav';

import { AgentCanvas } from './agents/canvas';
import {
  ActionConfirmationCard,
  type ActionRestage,
} from '@/components/assistant-ui/action-confirmation';
import { actionCopy } from '@/components/assistant-ui/action-copy';
import { useChatCopy, useChatInterfaceLocale } from '@/lib/chat-i18n';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import knowledgePagesCopy from '@/i18n/ns/knowledge-pages';
import { ActivityCanvas } from './activity-canvas';
import { useCanvasFocus } from './canvas-focus';
import { InboxCanvas, useInboxCanvasLabels } from './inbox-canvas';
import { useCanvasCopy } from './canvas-copy';
import { useAgentFeatures } from './agent-features';
import { consumeShareRequest, useAgentWayBack, wayBackKey } from './everywhere/agent-link';
import { CanvasMoreMenu } from './canvas-more-menu';
import { sameCanvasObject } from './canvas-navigation';
import { skillSharingPresentation } from './canvas-sharing';
import { artifactCanvasScope, artifactShareUrl, isArtifactCanvas } from './artifacts/routes';
import {
  ArtifactCanvasOutlet,
  CanvasItemNameContext,
  CanvasKindLabelContext,
  CanvasTitleContext,
} from './artifacts/outlet';
import { isAppPageMode } from './apps/app-page-mode';
import { appInitial } from './apps/apps-page';
import artifactPanelsPageCopy from '@/i18n/ns/artifact-panels';
import { AppPreview } from './apps/preview/app-preview';
import { NewAppCanvas } from './apps/app-create';
import { isNewAppCanvas } from './apps/new-app';
import { QuestionChoicesCanvas } from './question-choices-canvas';
import { StorefrontCanvas } from './storefront/storefront-canvas';
import { ProjectDraftCanvas } from './projects/draft-canvas';
import storefrontCopy from '@/i18n/ns/storefront';
import { useQuestionCopy } from './question-copy';
import { viewLabels } from './view-definitions';
import { CANVAS_ID, CanvasDivider } from './canvas-divider';

const RETIRED_SOURCE =
  'This source is no longer available. Company knowledge now lives in Knowledge pages, and files stay in the Library.';

// These canvases load and poll their own data inside their feature modules.
const selfLoadingCanvases = new Set([
  'app-preview',
  'artifact',
  'artifact-draft',
  'knowledge',
  'page',
  'page-proposal',
  'agent',
  'action',
  'activity',
  'web',
  'voice-preview',
  'inbox',
  'choices',
  'storefront',
  'project-draft',
]);
const canvasTitles = (uiText: WorkspaceText): Record<string, string> => ({
  'app-preview': uiText('App'),
  artifact: uiText('Artifact'),
  'artifact-draft': uiText('Artifact'),
  agent: uiText('Agent'),
  'project-draft': uiText('New project'),
  project: uiText('About this project'),
  action: uiText('Review request'),
  activity: uiText('Activity'),
  'voice-preview': uiText('Voice preview'),
  knowledge: uiText('Company knowledge'),
  skill: uiText('Skill'),
  source: uiText('Source'),
  web: uiText('Source'),
  file: uiText('File'),
  result: uiText('Result'),
  meeting: uiText('Meeting'),
  page: uiText('Knowledge page'),
  'page-proposal': uiText('Suggested knowledge update'),
});

type Detail = CompanyFile | CompanySource | CompanySkill | CompanyResult | CompanyMeeting;
/**
 * `restage` lives above the canvas: the canvas remounts per object, and a restage
 * swaps to a new request id. The shell holds the last restage the server returned,
 * so the replacing request can say it carries the person's edits.
 */
/** The preview header names the kind the producer reported; an unknown kind stays a neutral "Preview". */
function appPreviewTitle(
  kind: 'app' | 'deck' | undefined,
  panels: { kindApp: string; kindDeck: string },
  uiText: WorkspaceText = workspaceTextFor('en'),
) {
  return kind === 'deck' ? panels.kindDeck : kind === 'app' ? panels.kindApp : 'Preview';
}

export function WorkspaceCanvas({
  restage = null,
  onRestaged,
}: {
  restage?: ActionRestage | null;
  onRestaged?: (restage: ActionRestage) => void;
} = {}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const approvalCopy = actionCopy(useChatInterfaceLocale());
  const canvasCopy = useCanvasCopy();
  const agentFeatures = useAgentFeatures();
  const appsCopy = useNamespaceCopy(artifactPanelsPageCopy);
  const storefrontText = useNamespaceCopy(storefrontCopy);
  const questionCopy = useQuestionCopy().copy;
  const target = ws.canvas;
  const mobileSheet = useCanvasSheet(
    target?.kind ?? '',
    target?.id ?? '',
    () => ws.openCanvas(null),
    target?.full,
    // Esc leaves full screen the way collapse does: the same object, back in the workspace.
    target?.full ? () => ws.openCanvas({ ...target, full: false }) : ws.canvasBack,
  );
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const heading = useRef<HTMLDivElement>(null);
  const scroll = useRef<HTMLDivElement>(null);
  const loadedTarget = useRef('');
  const companyId = useCompany().data?.company?.id;
  // The person's connected agents a Result can be opened in (everywhere/agent-link.ts).
  const wayBack = useAgentWayBack(wayBackKey(agentFeatures?.everywhere, ws.user?.id, companyId));
  const targetKey = JSON.stringify([
    ws.user?.id,
    companyId,
    target?.kind,
    target?.id,
    ...artifactCanvasScope(target),
  ]);
  // The artifact canvas reports its kind so the header says "Document"; it is dropped with the target.
  const [kindLabel, setKindLabel] = useState<{ key: string; label: string } | null>(null);
  const reportKind = useCallback(
    (label: string | null) => setKindLabel(label ? { key: targetKey, label } : null),
    [targetKey],
  );
  // A saved App on the Apps view is its own page: it reports its name for the page header.
  const [pageTitle, setPageTitle] = useState<{ key: string; title: string } | null>(null);
  const reportTitle = useCallback(
    (name: string | null) => setPageTitle(name ? { key: targetKey, title: name } : null),
    [targetKey],
  );
  // The item's own name, for the chat's "In this chat" list (saved items report it).
  const [itemName, setItemName] = useState<{ key: string; name: string } | null>(null);
  const reportItemName = useCallback(
    (name: string | null) => setItemName(name ? { key: targetKey, name } : null),
    [targetKey],
  );
  const [footerHost, setFooterHost] = useState<HTMLDivElement | null>(null);
  const [headerHost, setHeaderHost] = useState<HTMLSpanElement | null>(null);
  const [leadHost, setLeadHost] = useState<HTMLSpanElement | null>(null);
  useCanvasFocus({
    active: Boolean(target),
    targetKey,
    autoOpened: ws.canvasAutoOpened,
    heading,
    canvas: mobileSheet.sheet,
    onClose: ws.canvasBack,
  });
  // An Inbox canvas is named by its item's kind; its back path by the item's title.
  const [inboxLabels, previousInbox] = useInboxCanvasLabels([
    target?.kind === 'inbox' ? target.id : null,
    ws.canvasPrevious?.kind === 'inbox' ? ws.canvasPrevious.id : null,
  ]);
  useEffect(() => {
    let active = true;
    if (!target || selfLoadingCanvases.has(target.kind) || target.id === 'new') {
      loadedTarget.current = targetKey;
      setError('');
      setLoading(false);
      return;
    }
    setError('');
    // Refresh the same object in place: unmounting it discards the current
    // slide, selection and editor state. A scope/object change still clears it.
    if (loadedTarget.current !== targetKey) {
      loadedTarget.current = '';
      setDetail(null);
      setLoading(true);
    }
    if (
      target.kind === 'knowledge' ||
      target.kind === 'page' ||
      target.kind === 'page-proposal' ||
      target.kind === 'agent'
    )
      return;
    const request =
      target.kind === 'file'
        ? workspaceApi.file(target.id)
        : target.kind === 'source'
          ? workspaceApi.source(target.id)
          : target.kind === 'skill'
            ? workspaceApi.skill(target.id)
            : target.kind === 'meeting'
              ? workspaceApi.meeting(target.id)
              : workspaceApi.result(target.id);
    void request
      .then((value) => {
        if (active) {
          loadedTarget.current = targetKey;
          setDetail(value);
        }
      })
      .catch((failure) => {
        if (!active) return;
        // Sources were retired (D69); an old source link can never load again.
        if (target.kind === 'source' && (failure as { status?: number })?.status === 404)
          setError(RETIRED_SOURCE);
        else
          setError(
            failure instanceof Error ? failure.message : uiText('We could not load this item.'),
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [targetKey, ws.data, retry]);
  useEffect(() => {
    // Recording, sealing and processing meetings, and ready meetings whose
    // notes or transcript repair are regenerating, keep refreshing.
    if (target?.kind !== 'meeting' || !meetingNeedsRefresh(detail as CompanyMeeting | null)) return;
    const timer = window.setTimeout(() => setRetry((value) => value + 1), 3000);
    return () => window.clearTimeout(timer);
  }, [target?.kind, detail]);
  useEffect(() => {
    if (target?.kind === 'meeting' && detail) announceMeetingUpdated();
  }, [target?.kind, detail]);
  useEffect(() => {
    if (scroll.current) scroll.current.scrollTop = ws.canvasScrollTop;
  }, [target?.kind, target?.id, target?.tab, ws.canvasScrollTop]);
  const noteHistory = ws.canvasHistory.note;
  const noteName = itemName?.key === targetKey ? itemName.name : null;
  const noteKind = kindLabel?.key === targetKey ? kindLabel.label : null;
  useEffect(() => {
    if (target)
      noteHistory(target, { name: noteName, kind: noteKind, unavailable: Boolean(error) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey, noteName, noteKind, Boolean(error)]);
  if (!target) return null;
  const kindName = kindLabel?.key === targetKey ? kindLabel.label : null;
  const visibleDetail = loadedTarget.current === targetKey ? detail : null;
  const title =
    target.kind === 'action'
      ? approvalCopy.review
      : target.kind === 'activity'
        ? canvasCopy.activity
        : target.kind === 'inbox'
          ? inboxLabels.kind
          : target.kind === 'choices'
            ? questionCopy.choices
            : target.kind === 'app-preview'
              ? appPreviewTitle(target.appKind, appsCopy, uiText)
              : target.kind === 'storefront'
                ? storefrontText.assistantTitle
                : isArtifactCanvas(target)
                  ? // Named for what it is once it says so ("Deck"); until then no generic "Artifact" over the loading state.
                    (kindName ?? '')
                  : (canvasTitles(uiText)[target.kind] ?? uiText('Result'));
  const previousObjectTitle = ws.canvasPrevious
    ? ws.canvasPrevious.kind === 'skill'
      ? (ws.data.skills.find((skill) => skill.id === ws.canvasPrevious?.id)?.draft.name ??
        canvasCopy.activity)
      : ws.canvasPrevious.kind === 'result'
        ? (ws.data.results.find((result) => result.id === ws.canvasPrevious?.id)?.title ??
          canvasTitles(uiText).result)
        : ws.canvasPrevious.kind === 'source'
          ? (ws.data.sources.find((source) => source.id === ws.canvasPrevious?.id)?.title ??
            canvasTitles(uiText).source)
          : ws.canvasPrevious.kind === 'file'
            ? (ws.data.files.find((file) => file.id === ws.canvasPrevious?.id)?.name ??
              canvasTitles(uiText).file)
            : ws.canvasPrevious.kind === 'inbox'
              ? previousInbox.title
              : ws.canvasPrevious.kind === 'web'
                ? (ws.canvasPrevious.title ?? webSourceHost(ws.canvasPrevious.id))
                : isArtifactCanvas(ws.canvasPrevious)
                  ? // An Artifact opened from the Library goes back to the Library, not to "Artifact".
                    uiText('Library')
                  : (canvasTitles(uiText)[ws.canvasPrevious.kind] ?? canvasCopy.back)
    : canvasCopy.back;
  // Back returns to a different object (CANVAS-NAVIGATION rule 2); a tab or
  // focus change of the same object is never a back target.
  const showBack = ws.canvasHasPrevious && !sameCanvasObject(ws.canvasPrevious, target);
  const copyLink = () => {
    const done = () => ws.notify(canvasCopy.linkCopied, null, 'success');
    const failed = () => ws.notify(canvasCopy.copyLinkFailed, null, 'error');
    if (!navigator.clipboard?.writeText) return failed();
    void navigator.clipboard
      .writeText(artifactShareUrl(window.location.href, target))
      .then(done, failed);
  };
  const viewToggle = target.full ? canvasCopy.exitFullView : canvasCopy.expand;
  const appPage = isAppPageMode(ws.view, target);
  const appName = pageTitle?.key === targetKey ? pageTitle.title : null;
  // Back names where the person came from; on a direct link it falls back to the item's kind home
  // (an App: Apps; a Deck or any other kind opened on the Apps place: the Library).
  const origin = ws.canvasOrigin;
  const kindHome =
    appPage && isArtifactCanvas(target) && kindName !== null && appName === null
      ? 'library'
      : 'apps';
  const backTo =
    origin === 'apps'
      ? 'apps'
      : origin === 'results' || origin === 'files'
        ? 'library'
        : origin
          ? 'previous'
          : kindHome;
  const backLabel =
    backTo === 'apps'
      ? appsCopy.libraryKindApps
      : backTo === 'library'
        ? appsCopy.libraryTitle
        : (viewLabels[origin ?? 'chat'] ?? canvasCopy.back);
  return (
    <CanvasFooterHost host={footerHost}>
      <CanvasSheetScrim scrimRef={mobileSheet.scrim} />
      <CanvasHeaderHost host={headerHost} leadHost={leadHost}>
        <aside
          id={CANVAS_ID}
          className={`ws-canvas ${appPage ? 'ws-canvas-app-page' : ''}`}
          ref={mobileSheet.sheet}
          data-kind={target.kind}
          data-canvas-sheet="true"
          aria-label={uiText('{value1} canvas', { value1: title || canvasTitles(uiText).artifact })}
        >
          <CanvasSheetHandle handleRef={mobileSheet.handle} />
          <CanvasDivider />
          {appPage ? (
            <div className="ws-canvas-top ws-app-page-top" ref={heading} tabIndex={-1}>
              <span>
                <button
                  type="button"
                  className="ws-canvas-back"
                  data-back-to={backTo}
                  aria-label={backLabel}
                  onClick={
                    !origin && kindHome === 'library'
                      ? () => ws.navigate('results', 'artifacts')
                      : ws.canvasBack
                  }
                >
                  <ChevronLeftIcon aria-hidden="true" />
                  <span>{backLabel}</span>
                </button>
                {appName && (
                  <>
                    <span className="ws-app-page-icon" aria-hidden="true">
                      {appInitial(appName)}
                    </span>
                    <h1 className="ws-app-page-name">{appName}</h1>
                  </>
                )}
              </span>
              <div>
                <span ref={setHeaderHost} />
              </div>
            </div>
          ) : (
            <div className="ws-canvas-top" ref={heading} tabIndex={-1}>
              <span>
                {showBack && (
                  <button
                    type="button"
                    className="ws-canvas-back"
                    aria-label={`${canvasCopy.back}: ${previousObjectTitle}`}
                    onClick={ws.canvasBack}
                  >
                    <ChevronLeftIcon aria-hidden="true" />
                    <span>{previousObjectTitle}</span>
                  </button>
                )}
                <CanvasHistoryArrows fallback={title} />
                <CanvasSwitcher target={target} title={title} />
                <span ref={setLeadHost} className="ws-canvas-lead" />
              </span>
              <div>
                <span ref={setHeaderHost} />
                {visibleDetail &&
                  !error &&
                  !loading &&
                  (target.kind === 'file' ||
                    target.kind === 'skill' ||
                    target.kind === 'result') && (
                    <ShareButton
                      kind={target.kind}
                      item={
                        target.kind === 'skill'
                          ? skillSharingPresentation(visibleDetail as CompanySkill)
                          : (visibleDetail as CompanyFile | CompanySource | CompanyResult)
                      }
                      openOnRequest={
                        target.kind === 'result' ? () => consumeShareRequest(target.id) : undefined
                      }
                    />
                  )}
                {/* Collaboration v1 (PR 3 §4): sharing a meeting is sharing its file.
                The recording's owner gets Share; everyone else sees who can see
                it (Make private for the owner and admins sits in the meeting
                header). */}
                {visibleDetail && !error && !loading && target.kind === 'meeting' && (
                  <MeetingShare meeting={visibleDetail as CompanyMeeting} />
                )}
                <CanvasMoreMenu
                  kind={target.kind}
                  loaded={Boolean(visibleDetail)}
                  wayBack={wayBack}
                  onCopyLink={copyLink}
                />
                <button
                  className="ws-icon-button ws-canvas-view-toggle"
                  title={viewToggle}
                  aria-label={viewToggle}
                  onClick={() => ws.openCanvas({ ...target, full: !target.full })}
                >
                  {target.full ? (
                    <Minimize2Icon aria-hidden="true" />
                  ) : (
                    <Maximize2Icon aria-hidden="true" />
                  )}
                </button>
                <button
                  className="ws-icon-button"
                  aria-label={canvasCopy.close}
                  onClick={() => ws.openCanvas(null)}
                >
                  <WorkspaceIcon name="close" size={16} />
                </button>
              </div>
            </div>
          )}
          <div
            className="ws-canvas-scroll"
            ref={scroll}
            onScroll={(event) => ws.setCanvasScroll(event.currentTarget.scrollTop)}
          >
            {error === RETIRED_SOURCE ? (
              <ErrorState message={uiText(RETIRED_SOURCE)} />
            ) : error ? (
              <ErrorState message={error} onRetry={() => setRetry((value) => value + 1)} />
            ) : loading ? (
              <LoadingState label={uiText('Loading {value1}…', { value1: title.toLowerCase() })} />
            ) : isArtifactCanvas(target) ? (
              <CanvasKindLabelContext.Provider value={reportKind}>
                <CanvasTitleContext.Provider value={reportTitle}>
                  <CanvasItemNameContext.Provider value={reportItemName}>
                    <ArtifactCanvasOutlet key={targetKey} target={target} />
                  </CanvasItemNameContext.Provider>
                </CanvasTitleContext.Provider>
              </CanvasKindLabelContext.Provider>
            ) : target.kind === 'app-preview' ? (
              // A new App's panel waits until the chat starts building, then shows the build there.
              <CanvasItemNameContext.Provider value={reportItemName}>
                <AppPreview
                  id={target.id}
                  threadId={ws.threadId ?? ''}
                  waiting={isNewAppCanvas(target) ? <NewAppCanvas /> : null}
                  pinned={target.kind === 'app-preview' && target.pinned === true}
                  editor={target.kind === 'app-preview' && target.editor === true}
                />
              </CanvasItemNameContext.Provider>
            ) : target.kind === 'knowledge' ? (
              <KnowledgeRootCanvas full={Boolean(target.full)} />
            ) : target.kind === 'page' ? (
              <KnowledgePageCanvas target={target} />
            ) : target.kind === 'page-proposal' ? (
              <PageProposalCanvas id={target.id} full={Boolean(target.full)} />
            ) : target.kind === 'agent' ? (
              <AgentCanvas target={target} />
            ) : target.kind === 'inbox' ? (
              <InboxCanvas id={target.id} />
            ) : target.kind === 'action' ? (
              <ActionConfirmationCard
                key={target.id}
                pointer={{ status: 'confirmation_required', action: { id: target.id } }}
                mode="canvas"
                restage={restage}
                onRestaged={onRestaged}
              />
            ) : target.kind === 'voice-preview' ? (
              <VoicePreview preview={target.preview} />
            ) : target.kind === 'choices' ? (
              <QuestionChoicesCanvas id={target.id} tab={target.tab} />
            ) : target.kind === 'activity' ? (
              <ActivityCanvas
                messageId={target.id}
                step={target.step}
                target={target as { kind: 'activity'; id: string; tab?: string; step?: string }}
              />
            ) : target.kind === 'web' ? (
              <WebSourceCanvas target={target} />
            ) : target.kind === 'project-draft' ? (
              <ProjectDraftCanvas threadId={target.id} />
            ) : target.kind === 'storefront' ? (
              <StorefrontCanvas key={target.id} storeId={target.id} />
            ) : target.kind === 'skill' ? (
              <SkillCanvas skill={visibleDetail as CompanySkill | null} target={target} />
            ) : target.kind === 'result' && visibleDetail ? (
              <ResultCanvas result={visibleDetail as CompanyResult} target={target} />
            ) : target.kind === 'meeting' && visibleDetail ? (
              <MeetingCanvas
                meeting={visibleDetail as CompanyMeeting}
                onRetry={() => setRetry((value) => value + 1)}
              />
            ) : target.kind === 'source' && visibleDetail ? (
              <SourceCanvas source={visibleDetail as CompanySource} />
            ) : target.kind === 'file' && visibleDetail ? (
              <FileCanvas file={visibleDetail as CompanyFile} />
            ) : null}
          </div>
          <div className="ws-canvas-footer-host" ref={setFooterHost} />
        </aside>
        <CanvasLeaveDialog
          open={ws.canvasLeavePrompt}
          onCancel={ws.cancelCanvasLeave}
          onDiscard={ws.discardCanvasLeave}
        />
      </CanvasHeaderHost>
    </CanvasFooterHost>
  );
}
function MeetingShare({ meeting }: { meeting: CompanyMeeting }) {
  const ws = useWorkspace();
  const { locale } = useWorkspaceText();
  return meeting.canShare ? (
    <ShareButton kind="file" noun="meeting" item={meetingShareItem(meeting)} />
  ) : (
    <small className="ws-muted">
      {audienceLabel(meetingShareItem(meeting), ws.companyName, locale)}
    </small>
  );
}
function FileCanvas({ file }: { file: CompanyFile }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const [tab, setTab] = useState('preview');
  const parsed = useMemo(() => {
    if (file.mimeType !== 'text/csv' || file.content === undefined) return null;
    try {
      return { table: parseFileCsv(file.content), error: '' };
    } catch (error) {
      return {
        table: null,
        error: error instanceof Error ? error.message : uiText('Could not preview this CSV.'),
      };
    }
  }, [file.mimeType, file.content]);
  const chart = parsed?.table ? tableChart(parsed.table) : null;
  return (
    <div className="ws-canvas-body">
      <div className="ws-eyebrow">
        {uiRich('{value1} · Original retained', {
          value1: audienceLabel(file, ws.companyName, uiText.locale),
        })}
      </div>
      <h1>{file.name}</h1>
      <p className="ws-muted">
        {uiRich('{value1} {value2}{value3} · Added {value4}', {
          value1: sizeLabel(file.sizeBytes),
          value2: file.pageCount
            ? ' · ' +
              uiText(file.pageCount === 1 ? '{count} page' : '{count} pages', {
                count: file.pageCount.toLocaleString(uiText.locale),
              })
            : '',
          value3: ' ',
          value4: dateLabel(file.createdAt, uiText.locale),
        })}
      </p>
      <div className="ws-actions">
        <button
          className="ws-button primary"
          disabled={Boolean(file.processingStatus && file.processingStatus !== 'ready')}
          onClick={() =>
            ws.send('', {
              ...emptyTurnContext(),
              fileIds: [file.id],
            })
          }
        >
          {uiRich('{value1} Use in chat', { value1: <WorkspaceIcon name="chat" size={15} /> })}
        </button>
        <button
          className="ws-button"
          onClick={() =>
            void downloadFile(file, uiText.locale).catch((error) => ws.notify(error.message))
          }
        >
          {uiRich('{value1} Download', { value1: <WorkspaceIcon name="download" size={15} /> })}
        </button>
        <DeleteLibraryItem kind="file" id={file.id} title={file.name} canDelete={file.canDelete} />
      </div>
      {parsed && (
        <div className="ws-view-tabs">
          <button
            className={tab === 'preview' ? 'active' : ''}
            aria-pressed={tab === 'preview'}
            onClick={() => setTab('preview')}
          >
            {uiText('Table')}
          </button>
          {chart && (
            <button
              className={tab === 'chart' ? 'active' : ''}
              aria-pressed={tab === 'chart'}
              onClick={() => setTab('chart')}
            >
              {uiText('Chart')}
            </button>
          )}
          <button
            className={tab === 'original' ? 'active' : ''}
            aria-pressed={tab === 'original'}
            onClick={() => setTab('original')}
          >
            {uiText('Original')}
          </button>
        </div>
      )}
      <div className="ws-source-content">
        <FileReadability file={file} />
        {file.mimeType === 'application/pdf' && (
          <p className="ws-page-note">
            {uiText(
              'Text extracted from the PDF, marked by page. Download the original for its layout and images.',
            )}
          </p>
        )}
        {file.contentTruncated && (!parsed || tab === 'original') && (
          <p className="ws-page-note" role="status">
            {uiText(
              'This preview shows the beginning of the file. Poneva reads the whole file; download the original to see all of it.',
            )}
          </p>
        )}
        {parsed?.table?.truncated && tab !== 'original' && (
          <p className="ws-page-note" role="status">
            {uiRich(
              'Preview of the first {value1} complete rows. Filtering and charts use only this preview. Download the original for the full file.',
              { value1: parsed.table.rows.length.toLocaleString(uiText.locale) },
            )}
          </p>
        )}
        {parsed && tab !== 'original' ? (
          parsed.error ? (
            <ErrorState message={parsed.error} />
          ) : tab === 'chart' && chart ? (
            <WorkspaceChart chart={chart} columns={parsed.table?.columns} />
          ) : parsed.table ? (
            <WorkspaceTable table={parsed.table} />
          ) : null
        ) : file.mimeType === 'text/markdown' ? (
          <SourceText key={file.id} text={file.content ?? ''} />
        ) : (
          <SourceText
            key={file.id}
            text={file.content ?? uiText('File content is unavailable.')}
            markdown={false}
          />
        )}
      </div>
    </div>
  );
}
function webSourceHost(id: string) {
  const url = safeSourceUrl(id);
  return url ? new URL(url).hostname : id;
}
/** An external page a chat answer cited: what was cited and where it lives, never a navigation away. */
function WebSourceCanvas({ target }: { target: { id: string; title?: string } }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const copy = useChatCopy();
  const url = safeSourceUrl(target.id);
  if (!url) return <ErrorState message={uiText('This source link cannot be opened.')} />;
  const host = new URL(url).hostname;
  return (
    <div className="ws-canvas-body" data-web-source={host}>
      <div className="ws-eyebrow">{host}</div>
      <h1>{target.title || host}</h1>
      <p className="ws-web-source-note">{copy.webSourceUnread}</p>
      <a className="ws-button" href={url} target="_blank" rel="noopener noreferrer">
        {copy.webSourceOpenOriginal} <WorkspaceIcon name="arrow" size={14} />
      </a>
    </div>
  );
}
function SourceCanvas({ source }: { source: CompanySource }) {
  const { text: uiText } = useWorkspaceText();
  const ws = useWorkspace();
  const knowledgeText = useNamespaceCopy(knowledgePagesCopy);
  const url = safeSourceUrl(source.url);
  return (
    <div className="ws-canvas-body">
      <div className="ws-eyebrow">
        {sourcePresentation(source.kind, knowledgeText).label} ·{' '}
        {audienceLabel(source, ws.companyName, uiText.locale)}
      </div>
      <h1>{source.title}</h1>
      <p className="ws-muted">
        {source.coverage || knowledgeText.sourceCanvasRecorded} ·{' '}
        {dateLabel(source.observedAt, uiText.locale)}
      </p>
      {url && (
        <a className="ws-text-button" href={url} target="_blank" rel="noopener noreferrer">
          {knowledgeText.sourceCanvasOpenOriginal} <WorkspaceIcon name="arrow" size={14} />
        </a>
      )}
      <div className="ws-source-content">
        <SourceText
          key={source.id}
          text={source.content ?? knowledgeText.sourceCanvasUnavailable}
        />
      </div>
      <button
        className="ws-button"
        onClick={() =>
          ws.send(formatCopy(knowledgeText.sourceCanvasDiscussPrompt, { title: source.title }), {
            ...emptyTurnContext(),
            sourceIds: [source.id],
          })
        }
      >
        <WorkspaceIcon name="chat" size={15} />
        {knowledgeText.sourceCanvasDiscuss}
      </button>
    </div>
  );
}
